<template>
  <Transition name="modal" :duration="300">
    <div
      class="relative z-50"
      role="dialog"
      aria-modal="true"
      v-show="state"
    >
      <div class="fixed inset-0 bg-gray-400 bg-opacity-75 transition-opacity" @click="close" />
      <div class="fixed inset-0 z-10 flex items-center justify-center p-4">
        <div
          class="relative flex w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl"
          style="max-height: 80vh"
        >
          <div class="flex items-center justify-between border-b border-greyLight px-4 py-3">
            <div class="truncate pr-3 text-sm font-semibold text-grey">
              {{ fileName }}
            </div>
            <button
              type="button"
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-greyLight text-grey"
              @click="close"
            >
              <i class="fas fa-times" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto bg-background p-4" style="min-height: 0">
            <div v-if="loading" class="flex flex-col items-center justify-center py-10 text-grey">
              <div
                class="h-8 w-8 animate-spin rounded-full border-b-2 border-[#FFD92E]"
              />
              <div class="mt-3 text-sm text-grey text-opacity-50">Загружаем документ...</div>
            </div>

            <div
              v-if="error"
              class="flex flex-col items-center justify-center py-10 text-center text-grey"
            >
              <i class="fas fa-file-excel text-3xl text-red" />
              <div class="mt-3 text-sm">Не удалось открыть документ</div>
              <div class="mt-1 text-xs text-grey text-opacity-50">{{ error }}</div>
            </div>

            <div ref="pdfContainer" class="pdf-pages" />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import * as pdfjsLib from "pdfjs-dist";
import { Options, Vue } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { markRaw } from "@vue/reactivity";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.BASE_URL}pdf.worker.min.mjs`;

@Options({})
export default class PdfPreviewModal extends Vue {
  @Prop() state!: boolean;
  @Prop() url!: string;
  @Prop() fileName!: string;

  private loading = false;
  private error = "";
  private pdfTask: pdfjsLib.PDFDocumentLoadingTask | null = null;

  get pdfContainer(): HTMLDivElement {
    return this.$refs.pdfContainer as HTMLDivElement;
  }

  @Watch("state")
  onStateChange(newValue: boolean) {
    if (newValue) {
      document.body.classList.add("overflow-hidden");
      this.renderPdf();
    } else {
      document.body.classList.remove("overflow-hidden");
      this.destroy();
    }
  }

  private async renderPdf() {
    if (!this.url) return;

    this.loading = true;
    this.error = "";
    this.pdfContainer.innerHTML = "";

    try {
      this.pdfTask = markRaw(pdfjsLib.getDocument(this.url));
      const pdf = await this.pdfTask.promise;

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const page = await pdf.getPage(pageNumber);

        const containerWidth = this.pdfContainer.clientWidth || 320;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(containerWidth / baseViewport.width, 2);

        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.className = "mb-3 w-full rounded-md bg-white shadow";

        this.pdfContainer.appendChild(canvas);

        const context = canvas.getContext("2d");
        if (!context) throw new Error("Canvas context not available");
        await page.render({ canvasContext: context, viewport }).promise;
      }
    } catch (e) {
      this.error = (e as Error)?.message || "Ошибка рендеринга";
    } finally {
      this.loading = false;
    }
  }

  private destroy() {
    try {
      this.pdfTask?.destroy();
    } catch (e) {
      console.error(e);
    }
    this.pdfTask = null;
    if (this.pdfContainer) {
      this.pdfContainer.innerHTML = "";
    }
  }

  private close() {
    this.$emit("close");
  }
}
</script>
